export default baseUrl => [
  {
    title: 'Payments',
    name: 'paymentsExport',
    description: 'Overview of all payments made in a specific period.',
    endpoint: `${baseUrl}/reports/finance/payments`,
    icon: 'icon-payment',
    customDateRangeLimit: 95,
    filterBy: filterWithDateOfBookingVisitPlusDateOfPaymentAndStatus(),
  },
  {
    title: 'Customers',
    name: 'customerExport',
    description: 'List of customers related to bookings in a specific period.',
    endpoint: `${baseUrl}/reports/finance/customers`,
    icon: 'icon-customer',
    filterBy: baseDateRangesPlusPrevMonth(),
  },
  {
    title: 'Product sales',
    name: 'productSalesExport',
    description:
      'Number of times a product is sold online and as admin booking in a specific period.',
    endpoint: `${baseUrl}/reports/finance/product-sales`,
    icon: 'icon-sales-sticker',
    filterBy: filterWithDateOfBookingAndDateOfVisit(),
  },
  {
    title: 'Deal sales',
    name: 'dealSalesExport',
    description: 'Number of times a deal is sold online in a specific period.',
    endpoint: `${baseUrl}/reports/finance/deal-sales`,
    icon: 'icon-sales-sticker-2',
    filterBy: filterWithDateOfBookingAndDateOfVisit(),
  },
  {
    title: 'Bookings',
    name: 'bookingsExport',
    description:
      'Overview of the actual status of bookings within a specific period. The export will contain the totals of a booking, not the booking products.',
    endpoint: `${baseUrl}/reports/finance/bookings`,
    icon: 'icon-shopping-cart',
    customDateRangeLimit: 95,
    filterBy: filterWithDateOfBookingAndDateOfVisit(),
  },
  {
    title: 'Booking mutations',
    name: 'bookingMutationsExport',
    description:
      'All mutations for bookings within a specific period. This export will contain the mutations of the booking totals or statuses, not the mutations on booking product level.',
    endpoint: `${baseUrl}/reports/finance/booking-mutations`,
    icon: 'icon-arrows-crossing',
    customDateRangeLimit: 95,
    filterBy: baseDateRangesPlusTomorrowAndUpcomingSevenDays(),
  },
  {
    title: 'Booking products',
    name: 'bookingProductsExport',
    description:
      'Overview of the actual status of products within bookings for a specific period. The export will contain the products of a booking, not the booking totals.',
    endpoint: `${baseUrl}/reports/finance/booking-products`,
    icon: 'icon-shopping-basket-with-sale',
    customDateRangeLimit: 95,
    filterBy: filterWithDateOfBookingAndDateOfVisit(),
  },
  {
    title: 'Booking product mutations',
    name: 'bookingProductMutationsExport',
    description:
      'All mutations for products within bookings for a specific period. This export will contain the mutations of the booking products, not the mutations on totals or statuses.',
    endpoint: `${baseUrl}/reports/finance/booking-product-mutations`,
    icon: 'icon-arrows-crossing',
    customDateRangeLimit: 95,
    filterBy: baseDateRangesPlusTomorrowAndUpcomingSevenDays(),
  },
  {
    title: 'Products',
    name: 'productsExport',
    description: 'Overview of the current products in Briq.',
    endpoint: `${baseUrl}/reports/finance/products`,
    icon: 'icon-sales-sticker-singular-empty',
    filterBy: { separator: true, dataCulture: true },
  },
  {
    title: 'Deals',
    name: 'dealsExport',
    description: 'Overview of the current deals in Briq.',
    endpoint: `${baseUrl}/reports/finance/deals`,
    icon: 'icon-sales-sticker-with-percentage-2',
    filterBy: { separator: true, dataCulture: true },
  },
  {
    title: 'Discount vouchers',
    name: 'vouchersExport',
    description: 'Overview of all redeemed discount vouchers in a specific period.',
    endpoint: `${baseUrl}/reports/finance/vouchers`,
    icon: 'icon-percentage',
    filterBy: baseDateRanges(),
  },
  {
    title: 'Capacity',
    name: 'capacityExport',
    description:
      'Overview of the current capacity per time slot of each  activity for a selected period.',
    endpoint: `${baseUrl}/reports/finance/capacity`,
    icon: 'icon-capacity-export',
    filterBy: baseDateRangesPlusTomorrow(),
  },
  {
    title: 'Balance & Payout',
    name: 'briqPayExport',
    description:
      'Reconcile your balance and get a categorized breakdown for each payout of your Briq Pay account',
    endpoint: `${baseUrl}/reports/finance/briq-pay`,
    icon: 'icon-briqpay-export',
    customDateRangeLimit: 180,
    filterBy: briqPayFilter(),
    dateRangeDisabledDates: ['today', 'yesterday'],
    maxDate: 'dayBeforeYesterday',
    exportNotification: 'The data for this export will be available after 2 days. Therefore, it is not possible to select today&rsquo;s date or yesterday&rsquo;s date.'
  },
]

function baseDateRanges() {
  return {
    timezone: true,
    daterange: {
      yesterday: true,
      lastSevenDays: true,
      custom: true,
    },
    separator: true,
    dataCulture: true,
    reportType: false,
  }
}

function briqPayBaseDateRanges() {
  return {
    timezone: true,
    daterange: {
      dayBeforeYesterday: true,
      previousMonth: true,
      custom: true,
    },
    separator: true,
    dataCulture: true,
    reportType: false,
  }
}

function briqPayFilter() {
  const options = briqPayBaseDateRanges()
  options.separator = false
  options.dataCulture = false
  options.reportType = true
  return options
}

function baseDateRangesPlusPrevMonth() {
  const options = baseDateRanges()
  options.daterange.previousMonth = true
  return options
}

function baseDateRangesPlusTomorrow() {
  const options = baseDateRanges()
  options.daterange.tomorrow = true
  return options
}

function baseDateRangesPlusTomorrowAndUpcomingSevenDays() {
  const options = baseDateRangesPlusTomorrow()
  options.daterange.upcomingSevenDays = true
  return options
}

function filterWithDateOfBookingAndDateOfVisit() {
  return {
    timezone: true,
    dateType: {
      dateOfBookingCreation: {
        yesterday: true,
        lastSevenDays: true,
        custom: true,
      },
      dateOfVisitBooking: {
        yesterday: true,
        lastSevenDays: true,
        tomorrow: true,
        upcomingSevenDays: true,
        custom: true,
      },
    },
    separator: true,
    dataCulture: true,
  }
}

function filterWithDateOfBookingVisitPlusDateOfPaymentAndStatus() {
  const options = filterWithDateOfBookingAndDateOfVisit()

  const aditionalData = {
    ...options.dateType,
    dateOfPaymentCreation: {
      yesterday: true,
      lastSevenDays: true,
      custom: true,
    },
    dateOfPaymentStatus: {
      yesterday: true,
      lastSevenDays: true,
      custom: true,
    },
  }

  options.dateType = aditionalData

  return options
}
